import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator, Download } from 'lucide-react';
import { calculatePartnerCommissions, getPartnerPayoutsCsv } from '../../services/api';
import { downloadCsv } from '../../utils/kardex';
import { isValidPeriod, payoutsCsvFileName, previousPeriod } from '../../utils/partnersAdmin';

/**
 * RES-91: calcular las comisiones de un período y descargar el CSV de payouts. El cálculo es
 * idempotente (recalcular no duplica) y no toca períodos ya aprobados o pagados.
 */
function PartnerPeriodTools({ onCalculated, onError, fmt }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(() => previousPeriod());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [summary, setSummary] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const valid = isValidPeriod(period);

  const openConfirm = () => {
    setSummary(null);
    setConfirmOpen(true);
  };

  const closeModal = () => {
    setConfirmOpen(false);
    setSummary(null);
  };

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const result = await calculatePartnerCommissions(period);
      setSummary(result || {});
      await onCalculated();
    } catch (error) {
      console.error('Error calculating partner commissions:', error);
      setConfirmOpen(false);
      onError(t('partners.calc.error', 'No se pudo calcular el período'));
    } finally {
      setCalculating(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const csv = await getPartnerPayoutsCsv(period);
      downloadCsv(typeof csv === 'string' ? csv : String(csv ?? ''), payoutsCsvFileName(period));
    } catch (error) {
      console.error('Error downloading payouts CSV:', error);
      onError(t('partners.calc.csvError', 'No se pudo descargar el CSV'));
    } finally {
      setDownloading(false);
    }
  };

  const periodLabel = fmt.period(summary?.period || period);

  return (
    <div className="partners-period-tools" data-testid="period-tools">
      <label htmlFor="partners-period" className="period-label">
        {t('partners.calc.period', 'Período')}
      </label>
      <input
        id="partners-period"
        name="period"
        type="month"
        value={period}
        onChange={(e) => setPeriod(e.target.value)}
      />
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        data-testid="calculate-period"
        disabled={!valid}
        onClick={openConfirm}
      >
        <Calculator size={14} /> {t('partners.calc.button', 'Calcular período')}
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        data-testid="download-csv"
        disabled={!valid || downloading}
        onClick={handleDownload}
      >
        <Download size={14} /> {t('partners.calc.csvButton', 'Descargar CSV de payouts')}
      </button>

      {confirmOpen && (
        <div className="modal-overlay">
          <div className="modal-content" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>
                {t('partners.calc.title', 'Calcular comisiones de {{period}}', {
                  period: periodLabel,
                })}
              </h2>
              <button
                type="button"
                className="close-btn"
                onClick={closeModal}
                aria-label={t('common.close', 'Cerrar')}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              {!summary ? (
                <>
                  <p>
                    {t(
                      'partners.calc.confirmText',
                      'Se calculan las comisiones de todos los partners para ese mes y se actualiza lo que hay que pagar.'
                    )}
                  </p>
                  <p className="field-hint">
                    {t(
                      'partners.calc.idempotentHint',
                      'Podés recalcular las veces que quieras: no se duplica nada. Los períodos ya aprobados o pagados no se tocan.'
                    )}
                  </p>
                </>
              ) : (
                <div className="calc-summary" data-testid="calc-summary">
                  <p>{t('partners.calc.done', 'Período calculado.')}</p>
                  <dl>
                    <div>
                      <dt>{t('partners.calc.partnersProcessed', 'Partners procesados')}</dt>
                      <dd data-testid="calc-processed">{summary.partnersProcessed ?? 0}</dd>
                    </div>
                    <div>
                      <dt>{t('partners.calc.totalPayable', 'Total a pagar')}</dt>
                      <dd data-testid="calc-payable">{fmt.money(summary.totalPayable ?? 0)}</dd>
                    </div>
                    <div>
                      <dt>
                        {t('partners.calc.totalRollover', 'Pasa al mes siguiente (arrastre)')}
                      </dt>
                      <dd data-testid="calc-rollover">{fmt.money(summary.totalRollover ?? 0)}</dd>
                    </div>
                  </dl>
                  {summary.lockedPeriods > 0 && (
                    <p className="alert alert-warning" data-testid="calc-locked">
                      {t(
                        'partners.calc.locked',
                        '{{count}} pagos de ese período ya estaban aprobados o pagados y no se recalcularon.',
                        { count: summary.lockedPeriods }
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="modal-actions">
              {!summary ? (
                <>
                  <button type="button" className="btn btn-ghost" onClick={closeModal}>
                    {t('common.cancel', 'Cancelar')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-testid="confirm-calculate"
                    disabled={calculating}
                    onClick={handleCalculate}
                  >
                    {calculating
                      ? t('partners.calc.calculating', 'Calculando…')
                      : t('partners.calc.confirm', 'Calcular')}
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-primary" onClick={closeModal}>
                  {t('common.close', 'Cerrar')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PartnerPeriodTools;
